import React, { useState, useRef, useEffect } from 'react'
import CustomIcons from './CustomIcons'
import StringSearchMatchDisplayer from './logic/StringSearchMatchDisplayer'
import theme from '../../styles/theme'
import {
  SearchContainer,
  SearchInput,
  SearchDropdown,
  DropdownItem,
  TagContainer,
  Tag,
  TagCloseButton,
  InputWrapper,
  TagWithIcon,
  ItemIcon,
  OverflowIndicator,
} from './styles/StandardSearchField.styles'

// Interface for data items that can be searched and selected
export interface SearchableItem {
  id: string | number
  name: string
  icon?: string
  isCreateOption?: boolean
}

// Props interface for the StandardSearchField component
interface StandardSearchFieldProps {
  data: SearchableItem[]
  onChange: (selectedItems: SearchableItem[]) => void
  placeholder?: string
  maxSelected?: number
  alwaysShowList?: boolean
  valueList?: SearchableItem[]
  singleSelection?: boolean
  style?: React.CSSProperties
  maxHeight?: number
  allowCreate?: boolean
  onCreate?: (newItemName: string) => void
  'data-testid'?: string
}

const StandardSearchField: React.FC<StandardSearchFieldProps> = ({
  data,
  onChange,
  placeholder = 'Search...',
  maxSelected = Infinity,
  alwaysShowList = false,
  valueList = [],
  singleSelection = true,
  style,
  maxHeight,
  allowCreate = false,
  onCreate,
  'data-testid': dataTestId,
}) => {
  const [searchText, setSearchText] = useState<string>('')
  const [showDropdown, setShowDropdown] = useState<boolean>(false)
  const [filteredItems, setFilteredItems] = useState<SearchableItem[]>([])
  const [hasIcons, setHasIcons] = useState<boolean>(false)
  const [hasOverflow, setHasOverflow] = useState<boolean>(false)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const tagContainerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // No need to set internal state since valueList is now the source of truth
  }, [data])

  useEffect(() => {
    if (!searchText && alwaysShowList) {
      setFilteredItems(
        data.filter(
          (item) => !valueList.some((selected) => selected.id === item.id),
        ),
      )
    } else if (!searchText) {
      setFilteredItems([])
      return
    }

    const lowercasedSearchText = searchText.toLowerCase()
    const filtered = data.filter(
      (item) =>
        item.name.toLowerCase().includes(lowercasedSearchText) &&
        (!singleSelection ||
          !valueList.some((selected) => selected.id === item.id)),
    )

    if (allowCreate && searchText && filtered.length === 0) {
      const exactMatchExists = data.some(
        (item) => item.name.toLowerCase() === lowercasedSearchText,
      )
      if (!exactMatchExists) {
        const createItem: SearchableItem = {
          id: `__create__${searchText}`,
          name: `Create "${searchText}"`,
          isCreateOption: true,
        }
        setFilteredItems([createItem])
        return // Exit early
      }
    }

    setFilteredItems(filtered)
  }, [
    searchText,
    data,
    valueList,
    alwaysShowList,
    singleSelection,
    allowCreate,
  ])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (data && data.length > 0) {
      setHasIcons(data.some((item) => item.icon))
    }
  }, [data])

  useEffect(() => {
    // Only check for overflow if maxHeight is set
    if (!maxHeight || !wrapperRef.current || !tagContainerRef.current) return

    const checkForOverflow = () => {
      // Add null checks to prevent errors when component is unmounting
      if (!wrapperRef.current || !tagContainerRef.current) return

      const wrapperHeight = wrapperRef.current.offsetHeight
      const contentHeight = tagContainerRef.current.offsetHeight + 25 // Add padding

      setHasOverflow(contentHeight > maxHeight && contentHeight > wrapperHeight)
    }

    checkForOverflow()

    // Re-check whenever valueList changes
    const resizeObserver = new ResizeObserver(checkForOverflow)
    resizeObserver.observe(tagContainerRef.current)

    return () => {
      if (tagContainerRef.current) {
        /* eslint-disable-next-line */
        resizeObserver.unobserve(tagContainerRef.current)
      }
      resizeObserver.disconnect()
    }
  }, [maxHeight, valueList])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
    setShowDropdown(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Check if backspace is pressed and the input is empty
    if (e.key === 'Backspace' && !searchText && valueList.length > 0) {
      // Remove the last item from the list
      const newSelected = [...valueList]
      newSelected.pop()
      onChange(newSelected)
    }
  }

  const handleSelectItem = (item: SearchableItem) => {
    if (valueList.length >= maxSelected) return

    // Just notify parent about the change, don't update internal state
    onChange([...valueList, item])
    setSearchText('')
    setShowDropdown(false)

    // Focus the input field after adding an item
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 0)
  }

  const handleRemoveTag = (itemToRemove: SearchableItem) => {
    // Just notify parent about the change, don't update internal state
    const newSelected = valueList.filter((item) => item.id !== itemToRemove.id)
    onChange(newSelected)
  }

  const handleInputFocus = () => {
    if (alwaysShowList && valueList.length < maxSelected) {
      setShowDropdown(true)
      setFilteredItems(
        data.filter(
          (item) =>
            !singleSelection ||
            !valueList.some((selected) => selected.id === item.id),
        ),
      )
    }
  }

  // Simplified hover and focus handlers
  const handleMouseEnter = () => {
    setIsExpanded(true)
  }

  const handleMouseLeave = () => {
    if (!containerRef.current?.contains(document.activeElement)) {
      setIsExpanded(false)
    }
  }

  const handleFocus = () => {
    setIsExpanded(true)
  }

  const handleBlur = () => {
    // Only collapse if the focus is leaving the entire component
    if (!containerRef.current?.matches(':hover')) {
      setIsExpanded(false)
    }
  }

  return (
    <SearchContainer
      style={{ ...style }}
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      data-testid={dataTestId}
    >
      <InputWrapper ref={wrapperRef} maxHeight={maxHeight}>
        <div style={{ margin: '0.1rem 0.1rem 0.0rem 0.1rem' }}>
          <CustomIcons.faSearch
            size={'small'}
            color={theme.colors.backgroundVeryDark}
            noHover={true}
          />
        </div>
        <TagContainer ref={tagContainerRef}>
          {valueList.map((item) =>
            hasIcons ? (
              <TagWithIcon
                key={item.id}
                data-testid={
                  dataTestId ? `${dataTestId}-tag-${item.id}` : undefined
                }
              >
                {item.icon && <img src={item.icon} alt="" />}
                {item.name}
                <TagCloseButton
                  type="button"
                  onClick={() => handleRemoveTag(item)}
                  data-testid={
                    dataTestId
                      ? `${dataTestId}-tag-${item.id}-remove`
                      : undefined
                  }
                >
                  ×
                </TagCloseButton>
              </TagWithIcon>
            ) : (
              <Tag
                key={item.id}
                data-testid={
                  dataTestId ? `${dataTestId}-tag-${item.id}` : undefined
                }
              >
                {item.name}
                <TagCloseButton
                  type="button"
                  onClick={() => handleRemoveTag(item)}
                  data-testid={
                    dataTestId
                      ? `${dataTestId}-tag-${item.id}-remove`
                      : undefined
                  }
                >
                  ×
                </TagCloseButton>
              </Tag>
            ),
          )}
          {valueList.length < maxSelected && (
            <SearchInput
              ref={inputRef}
              value={searchText}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              placeholder={
                valueList.length === 0 ? placeholder : 'Select more?'
              }
              data-testid={dataTestId ? `${dataTestId}-input` : undefined}
            />
          )}
        </TagContainer>
      </InputWrapper>

      <OverflowIndicator show={hasOverflow && !!maxHeight && !isExpanded} />

      {showDropdown &&
        filteredItems.length > 0 &&
        valueList.length < maxSelected && (
          <SearchDropdown
            ref={dropdownRef}
            data-testid={dataTestId ? `${dataTestId}-dropdown` : undefined}
          >
            {filteredItems.map((item) => (
              <DropdownItem
                key={item.id}
                onClick={() => {
                  if (item.isCreateOption) {
                    onCreate?.(searchText)
                    setSearchText('')
                    setShowDropdown(false)
                  } else {
                    handleSelectItem(item)
                  }
                }}
                data-testid={
                  dataTestId ? `${dataTestId}-option-${item.name}` : undefined
                }
              >
                {item.isCreateOption ? (
                  item.name
                ) : (
                  <>
                    {hasIcons && item.icon && (
                      <ItemIcon src={item.icon} alt="" />
                    )}
                    <StringSearchMatchDisplayer
                      searchTerms={searchText.split(/\s+/).filter(Boolean)}
                    >
                      {item.name}
                    </StringSearchMatchDisplayer>
                  </>
                )}
              </DropdownItem>
            ))}
          </SearchDropdown>
        )}
    </SearchContainer>
  )
}

export default StandardSearchField
